import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import type { Initiative } from '../../../../../api/initiative-client/initiative-client.type';
import { InitiativeStatus } from '../../../../../api/initiative-client/initiative-client.type';
import type { TableColumn } from '../../../../../components/table/table';
import { downloadCSV } from '../../../../../helpers/csv-download';
import { formatDate } from '../../../../../helpers/date-time-helpers';
import { Text } from '../../../../../ui-library/typography/typography';
import { getStatusColor } from '../../../filters/initiative-overview/initiative-overview.helpers';
import { StatusLabel } from '../../../filters/initiative-overview/status-label';
import type { Row } from './table.type';

/**
 * Transforms raw initiative data into structured table rows
 *
 * @param {Initiative[] | undefined} initiatives - Array of raw initiative data
 * @returns {Row[]} Array of structured initiative overview table rows
 */
const transformData = (initiatives: Initiative[] | undefined): Row[] => {
  if (!initiatives || initiatives.length === 0) {
    return [];
  }

  return initiatives.map((initiative) => ({
    id: initiative.id,
    name: initiative.name,
    status: initiative.status?.by_tasks?.status || InitiativeStatus.Unknown,
    completion: Math.round(initiative.percent_progress || 0),
    businessValue: initiative.relative_business_value ?? null,
    startDate: initiative.start_date || null,
    endDate: initiative.end_date || null,
  }));
};

/**
 * Returns status display text with appropriate styling
 *
 * @param {InitiativeStatus} status - Initiative status
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {ReactNode} React node with status indicator
 */
const renderStatus = (status: InitiativeStatus, t: TFunction): ReactNode => {
  const statusMap = {
    [InitiativeStatus.OnTrack]: {
      text: t('strategy.initiatives.statuses.on_track'),
      color: getStatusColor(InitiativeStatus.OnTrack),
    },
    [InitiativeStatus.AtRisk]: {
      text: t('strategy.initiatives.statuses.at_risk'),
      color: getStatusColor(InitiativeStatus.AtRisk),
    },
    [InitiativeStatus.OffTrack]: {
      text: t('strategy.initiatives.statuses.off_track'),
      color: getStatusColor(InitiativeStatus.OffTrack),
    },
    [InitiativeStatus.Unknown]: {
      text: t('strategy.initiatives.statuses.unknown'),
      color: getStatusColor(InitiativeStatus.Unknown),
    },
    [InitiativeStatus.Empty]: {
      text: t('strategy.initiatives.statuses.not_started'),
      color: getStatusColor(InitiativeStatus.Empty),
    },
  };

  const statusInfo = statusMap[status] || statusMap[InitiativeStatus.Unknown];

  return <StatusLabel color={statusInfo.color} label={statusInfo.text} />;
};

/**
 * Returns an array of column configurations for the initiative overview table
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {TableColumn<Row>[]} Array of column configurations
 */
const getTableColumns = (t: TFunction): TableColumn<Row>[] => [
  {
    key: 'name',
    label: t('common.initiatives'),
    sortable: true,
    width: '38%',
    render: (value) => <Text size="small">{value as string}</Text>,
  },
  {
    key: 'status',
    label: t('common.status.status'),
    sortable: true,
    width: '10%',
    render: (value) => renderStatus(value as InitiativeStatus, t),
  },
  {
    key: 'completion',
    label: t('strategy.initiatives.table.completion'),
    sortable: true,
    width: '12%',
    render: (value) => <Text size="small">{value}%</Text>,
  },
  {
    key: 'businessValue',
    label: t('strategy.initiatives.table.business_value'),
    sortable: true,
    width: '16%',
    render: (value) => <Text size="small">{value ?? t('strategy.initiatives.labels.no_data')}</Text>,
  },
  {
    key: 'startDate',
    label: t('strategy.initiative_performance.start_date'),
    sortable: true,
    width: '12%',
    render: (value) => (
      <Text size="small">
        {formatDate(value as Row['startDate'], { fallback: t('strategy.initiatives.labels.no_data') })}
      </Text>
    ),
  },
  {
    key: 'endDate',
    label: t('strategy.initiative_performance.end_date'),
    sortable: true,
    width: '12%',
    render: (value) => (
      <Text size="small">
        {formatDate(value as Row['endDate'], { fallback: t('strategy.initiatives.labels.no_data') })}
      </Text>
    ),
  },
];

/**
 * Custom sorting function for initiative overview table data
 *
 * @param {Row[]} data - Array of initiative overview table rows to sort
 * @param {keyof Row} sortBy - Column key to sort by
 * @param {'asc' | 'desc'} direction - Sort direction (asc or desc)
 * @returns {Row[]} Sorted array of initiative overview table rows
 */
const customSort = (data: Row[], sortBy: keyof Row, direction: 'asc' | 'desc'): Row[] => {
  return [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    // Handle null values - always put empty values at the bottom
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // String comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      return direction === 'asc' ? comparison : -comparison;
    }

    // Number comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      const comparison = aValue - bValue;
      return direction === 'asc' ? comparison : -comparison;
    }

    // Date comparison
    if (sortBy === 'startDate' || sortBy === 'endDate') {
      const comparison = dayjs(aValue).diff(dayjs(bValue));
      return direction === 'asc' ? comparison : -comparison;
    }

    return 0;
  });
};

/**
 * Returns status display text for CSV export
 *
 * @param {InitiativeStatus} status - Initiative status
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} Status text for CSV
 */
const getStatusText = (status: InitiativeStatus, t: TFunction): string => {
  const statusMap = {
    [InitiativeStatus.OnTrack]: t('strategy.initiatives.statuses.on_track'),
    [InitiativeStatus.AtRisk]: t('strategy.initiatives.statuses.at_risk'),
    [InitiativeStatus.OffTrack]: t('strategy.initiatives.statuses.off_track'),
    [InitiativeStatus.Unknown]: t('strategy.initiatives.statuses.unknown'),
    [InitiativeStatus.Empty]: t('strategy.initiatives.statuses.not_started'),
  };

  return statusMap[status] || statusMap[InitiativeStatus.Unknown];
};

/**
 * Exports initiative overview data as CSV
 *
 * @param {Row[]} data - Array of initiative overview table rows
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @return {void}
 */
const exportInitiativeOverviewCSV = (data: Row[], t: TFunction): void => {
  try {
    const csvData: (string | number)[][] = [];

    // Add header row
    const headerRow = [
      t('common.initiatives'),
      t('common.status.status'),
      t('strategy.initiatives.table.completion'),
      t('strategy.initiatives.table.business_value'),
      t('strategy.initiative_performance.start_date'),
      t('strategy.initiative_performance.end_date'),
    ];
    csvData.push(headerRow);

    // Add data rows
    data.forEach((row) => {
      const dataRow = [
        row.name,
        getStatusText(row.status, t),
        `${row.completion}%`,
        row.businessValue ?? t('strategy.initiatives.labels.no_data'),
        formatDate(row.startDate, { fallback: t('strategy.initiatives.labels.no_data') }),
        formatDate(row.endDate, { fallback: t('strategy.initiatives.labels.no_data') }),
      ];
      csvData.push(dataRow);
    });

    downloadCSV(csvData, 'initiative-overview.csv');
  } catch (error) {
    console.error('Error generating initiative overview CSV data:', error);
  }
};

export { customSort, exportInitiativeOverviewCSV, getTableColumns, transformData };
